import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCreditCard,
    faUniversity,
    faMoneyBillWave,
    faMobile,
    faInfoCircle,
    faWallet,
    faCopy,
    faCheck
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import UploadImages from '../UploadImages/UploadImages';

const PaymentMethods = ({
    bookingPolicies,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    bookingData,
    setBookingData
}) => {
    const { t } = useTranslation();
    const [showPaymentDetails, setShowPaymentDetails] = useState({});
    const [copiedText, setCopiedText] = useState({});

    // Copy to clipboard function
    const copyToClipboard = async (text, fieldId) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedText(prev => ({ ...prev, [fieldId]: true }));

            // Reset copied state after 2 seconds
            setTimeout(() => {
                setCopiedText(prev => ({ ...prev, [fieldId]: false }));
            }, 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
                setCopiedText(prev => ({ ...prev, [fieldId]: true }));

                setTimeout(() => {
                    setCopiedText(prev => ({ ...prev, [fieldId]: false }));
                }, 2000);
            } catch (err) {
                console.error('Fallback: Oops, unable to copy', err);
            }
            document.body.removeChild(textArea);
        }
    };

    // Get payment methods from booking policies
    const paymentMethods = bookingPolicies?.paymentPolicy?.paymentMethodsInfo || {};

    // Available payment method configurations
    const paymentMethodConfig = {
        instapay: {
            id: 'instapay',
            label: 'InstaPay',
            icon: faWallet,
            color: 'text-blue-600',
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-200',
            enabled: paymentMethods.instapay?.enabled,
            details: paymentMethods.instapay
        },
        bankTransfer: {
            id: 'bankTransfer',
            label: 'تحويل بنكي',
            icon: faUniversity,
            color: 'text-green-600',
            bgColor: 'bg-green-50',
            borderColor: 'border-green-200',
            enabled: paymentMethods.bankTransfer?.enabled,
            details: paymentMethods.bankTransfer
        },
        digitalWallet: {
            id: 'digitalWallet',
            label: 'محفظة رقمية',
            icon: faCreditCard,
            color: 'text-purple-600',
            bgColor: 'bg-purple-50',
            borderColor: 'border-purple-200',
            enabled: paymentMethods.digitalWallet?.enabled,
            details: paymentMethods.digitalWallet
        },
        cash: {
            id: 'cash',
            label: 'دفع نقدي',
            icon: faMoneyBillWave,
            color: 'text-yellow-600',
            bgColor: 'bg-yellow-50',
            borderColor: 'border-yellow-200',
            enabled: paymentMethods.cash?.enabled,
            details: paymentMethods.cash
        },
    };

    // Filter enabled payment methods
    const enabledPaymentMethods = Object.values(paymentMethodConfig).filter(method => method.enabled);

    const handlePaymentMethodChange = (methodId) => {
        setSelectedPaymentMethod(methodId);
        setBookingData((prev) => ({ ...prev, orgPaymentMethod: methodId }));
        // Toggle payment details visibility
        setShowPaymentDetails(prev => ({
            ...prev,
            [methodId]: !prev[methodId]
        }));

        // Hide details for other methods
        Object.keys(showPaymentDetails).forEach(key => {
            if (key !== methodId) {
                setShowPaymentDetails(prev => ({ ...prev, [key]: false }));
            }
        });
    };

    const renderPaymentDetails = (method) => {
        const { id, details } = method;

        if (!showPaymentDetails[id] || !selectedPaymentMethod || selectedPaymentMethod !== id) {
            return null;
        }

        return (
            <div className="mt-2 sm:mt-4 p-2 sm:p-4 rounded-lg border border-blue-200 bg-blue-50 animate-fadeIn">
                {/* Payment Method Specific Details */}
                {id === 'instapay' && details.instapayAddress && (
                    <div className="space-y-2 sm:space-y-4">
                        <div className="flex items-center">
                            <FontAwesomeIcon icon={faInfoCircle} className={`${method.color} text-sm sm:text-lg ms-2`} />
                            <h6 className="font-semibold text-blue-800 m-0 text-xs sm:text-base">عنوان InstaPay:</h6>
                        </div>
                        
                        {/* Mobile-optimized copy container */}
                        <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                            <div
                                className="p-3 sm:p-4   text-xs sm:text-base lg:text-lg text-blue-900 font-semibold cursor-pointer hover:bg-blue-50 active:bg-blue-100 transition-all duration-200 min-h-[52px] flex items-center touch-manipulation"
                                onClick={() => copyToClipboard(details.instapayAddress, `instapay-${id}`)}
                                title="انقر للنسخ"
                            >
                                <div className="flex items-center justify-between w-full">
                                    <div className="flex-1 min-w-0 pr-2">
                                        <div className="select-all break-all leading-tight">{details.instapayAddress}</div>
                                        {/* Mobile copy indicator */}
                                        <div className="text-xs text-blue-600 mt-1 sm:hidden">اضغط للنسخ</div>
                                    </div>
                                    <div className="flex-shrink-0 ml-2">
                                        <div className={`w-8 h-8 sm:w-6 sm:h-6 rounded-full flex items-center justify-center transition-all duration-200 ${copiedText[`instapay-${id}`] ? 'bg-green-100' : 'bg-blue-100'}`}>
                                            <FontAwesomeIcon
                                                icon={copiedText[`instapay-${id}`] ? faCheck : faCopy}
                                                className={`text-xs sm:text-sm ${copiedText[`instapay-${id}`] ? 'text-green-600' : 'text-blue-600'}`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-blue-100 p-2 sm:p-3 rounded-lg border border-blue-200">
                            <p className="text-blue-800 text-xs leading-relaxed">
                                اضغط على العنوان أعلاه لنسخه، ثم استخدمه في تطبيق InstaPay لإرسال المبلغ
                            </p>
                        </div>
                    </div>
                )}

                {id === 'bankTransfer' && details.bankDetails && (
                    <div className="space-y-2 sm:space-y-4">
                        <div className="flex items-center">
                            <FontAwesomeIcon icon={faInfoCircle} className={`${method.color} text-sm sm:text-lg ms-2`} />
                            <h6 className="font-semibold text-green-800 m-0 text-xs sm:text-base">بيانات الحساب البنكي:</h6>
                        </div>
                        
                        <div className="bg-white rounded-lg border border-green-200 overflow-hidden">
                            {details.bankDetails.accountName && (
                                <div className="border-b border-green-100 last:border-b-0">
                                    <div
                                        className="p-3 cursor-pointer hover:bg-green-50 active:bg-green-100 transition-all duration-200 min-h-[48px] flex items-center touch-manipulation"
                                        onClick={() => copyToClipboard(details.bankDetails.accountName, `accountName-${id}`)}
                                        title="انقر للنسخ"
                                    >
                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center w-full gap-2">
                                            <span className="text-gray-600 font-medium text-xs sm:text-base flex-shrink-0">اسم الحساب:</span>
                                            <div className="flex items-center justify-between flex-1 min-w-0">
                                                <span className="select-all text-xs sm:text-base break-all text-green-900 font-semibold pr-2">{details.bankDetails.accountName}</span>
                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center ml-2 transition-all duration-200 ${copiedText[`accountName-${id}`] ? 'bg-green-100' : 'bg-gray-100'}`}>
                                                    <FontAwesomeIcon
                                                        icon={copiedText[`accountName-${id}`] ? faCheck : faCopy}
                                                        className={`text-xs ${copiedText[`accountName-${id}`] ? 'text-green-700' : 'text-green-600'}`}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {details.bankDetails.accountNumber && (
                                <div className="border-b border-green-100 last:border-b-0">
                                    <div
                                        className="p-3 cursor-pointer hover:bg-green-50 active:bg-green-100 transition-all duration-200 min-h-[48px] flex items-center touch-manipulation"
                                        onClick={() => copyToClipboard(details.bankDetails.accountNumber, `accountNumber-${id}`)}
                                        title="انقر للنسخ"
                                    >
                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center w-full gap-2">
                                            <span className="text-gray-600 font-medium text-xs sm:text-base flex-shrink-0">رقم الحساب:</span>
                                            <div className="flex items-center justify-between flex-1 min-w-0">
                                                <span className="select-all text-xs sm:text-base break-all text-green-900 font-semibold   pr-2">{details.bankDetails.accountNumber}</span>
                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center ml-2 transition-all duration-200 ${copiedText[`accountNumber-${id}`] ? 'bg-green-100' : 'bg-gray-100'}`}>
                                                    <FontAwesomeIcon
                                                        icon={copiedText[`accountNumber-${id}`] ? faCheck : faCopy}
                                                        className={`text-xs ${copiedText[`accountNumber-${id}`] ? 'text-green-700' : 'text-green-600'}`}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {details.bankDetails.bankName && (
                                <div>
                                    <div
                                        className="p-3 cursor-pointer hover:bg-green-50 active:bg-green-100 transition-all duration-200 min-h-[48px] flex items-center touch-manipulation"
                                        onClick={() => copyToClipboard(details.bankDetails.bankName, `bankName-${id}`)}
                                        title="انقر للنسخ"
                                    >
                                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center w-full gap-2">
                                            <span className="text-gray-600 font-medium text-xs sm:text-base flex-shrink-0">اسم البنك:</span>
                                            <div className="flex items-center justify-between flex-1 min-w-0">
                                                <span className="select-all text-xs sm:text-base break-all text-green-900 font-semibold pr-2">{details.bankDetails.bankName}</span>
                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center ml-2 transition-all duration-200 ${copiedText[`bankName-${id}`] ? 'bg-green-100' : 'bg-gray-100'}`}>
                                                    <FontAwesomeIcon
                                                        icon={copiedText[`bankName-${id}`] ? faCheck : faCopy}
                                                        className={`text-xs ${copiedText[`bankName-${id}`] ? 'text-green-700' : 'text-green-600'}`}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <div className="bg-green-100 p-2 sm:p-3 rounded-lg border border-green-200">
                            <p className="text-green-800 text-xs leading-relaxed">
                                اضغط على أي من البيانات أعلاه لنسخها واستخدامها في التحويل البنكي
                            </p>
                        </div>
                    </div>
                )}

                {id === 'digitalWallet' && details.walletNumber && (
                    <div className="space-y-2 sm:space-y-4">
                        <div className="flex items-center">
                            <FontAwesomeIcon icon={faInfoCircle} className={`${method.color} text-sm sm:text-lg ms-2`} />
                            <h6 className="font-semibold text-purple-800 m-0 text-xs sm:text-base">رقم المحفظة الرقمية:</h6>
                        </div>
                        
                        <div className="bg-white rounded-lg border border-purple-200 overflow-hidden">
                            <div
                                className="p-3 sm:p-4   text-xs sm:text-base lg:text-lg text-purple-900 font-semibold cursor-pointer hover:bg-purple-50 active:bg-purple-100 transition-all duration-200 min-h-[52px] flex items-center touch-manipulation"
                                onClick={() => copyToClipboard(details.walletNumber, `walletNumber-${id}`)}
                                title="انقر للنسخ"
                            >
                                <div className="flex items-center justify-between w-full">
                                    <div className="flex-1 min-w-0 pr-2">
                                        <div className="select-all break-all leading-tight">{details.walletNumber}</div>
                                        <div className="text-xs text-purple-600 mt-1 sm:hidden">اضغط للنسخ</div>
                                    </div>
                                    <div className="flex-shrink-0 ml-2">
                                        <div className={`w-8 h-8 sm:w-6 sm:h-6 rounded-full flex items-center justify-center transition-all duration-200 ${copiedText[`walletNumber-${id}`] ? 'bg-green-100' : 'bg-purple-100'}`}>
                                            <FontAwesomeIcon
                                                icon={copiedText[`walletNumber-${id}`] ? faCheck : faCopy}
                                                className={`text-xs sm:text-sm ${copiedText[`walletNumber-${id}`] ? 'text-green-600' : 'text-purple-600'}`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-purple-100 p-2 sm:p-3 rounded-lg border border-purple-200">
                            <p className="text-purple-800 text-xs leading-relaxed">
                                اضغط على الرقم أعلاه لنسخه واستخدمه لإرسال المبلغ عبر المحفظة الرقمية
                            </p>
                        </div>
                    </div>
                )}

                {id === 'cash' && (
                    <div className="space-y-2 sm:space-y-4">
                        <div className="flex items-center">
                            <FontAwesomeIcon icon={faInfoCircle} className={`${method.color} text-sm sm:text-lg ms-2`} />
                            <h6 className="font-semibold text-orange-800 m-0 text-xs sm:text-base">الدفع النقدي:</h6>
                        </div>
                        <div className="bg-orange-100 p-3 rounded-lg border border-orange-200">
                            <p className="text-orange-800 text-xs sm:text-sm leading-relaxed">
                                سيتم تحصيل المبلغ نقدياً عند الوصول. تأكد من إحضار المبلغ المطلوب معك.
                            </p>
                        </div>
                    </div>
                )}

                {/* Enhanced Upload Payment Proof Section */}
                {id !== 'cash' && (
                    <div className="mt-3 sm:mt-6 p-2 sm:p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200">
                        <div className="flex items-center mb-2 sm:mb-3">
                            <FontAwesomeIcon icon={faInfoCircle} className="text-orange-600 text-sm sm:text-lg ms-2" />
                            <h6 className="font-semibold text-orange-800 m-0 text-xs sm:text-base">إثبات الدفع:</h6>
                        </div>
                        <div className="bg-white p-2 sm:p-4 rounded-lg border border-orange-200">
                            <div className="bg-orange-100 p-2 sm:p-3 rounded-lg border border-orange-200 mb-2 sm:mb-3">
                                <p className="text-orange-800 text-xs leading-relaxed">
                                    يرجى رفع صورة إيصال الدفع أو لقطة شاشة من العملية لتأكيد الحجز
                                </p>
                            </div>
                            <UploadImages
                                unitCode={`payment-${id}`}
                                pathImage={`payments`}
                                bookingData={bookingData}
                                setBookingData={setBookingData}
                                keyImages={"paymentImage"}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (!enabledPaymentMethods.length) {
        return (
            <div className="text-center py-6 sm:py-8 bg-yellow-50 rounded-lg border border-yellow-200">
                <div className="text-yellow-600 text-2xl sm:text-4xl mb-3">⚠️</div>
                <p className="text-yellow-800 font-medium text-xs sm:text-base">لا توجد طرق دفع متاحة حالياً</p>
            </div>
        );
    }

    return (
        <div className="space-y-2 sm:space-y-4">
            <div className="space-y-2">
                {enabledPaymentMethods.map((method) => (
                    <div key={method.id} className="space-y-1 sm:space-y-2">
                        {/* Enhanced Radio Button Card - Mobile First */}
                        <div
                            className={`relative cursor-pointer transition-all duration-300 rounded-xl border-2 p-3 hover:shadow-md active:scale-[0.98] touch-manipulation ${selectedPaymentMethod === method.id
                                    ? 'border-blue-500 bg-blue-50 shadow-lg transform scale-[1.01] sm:scale-[1.02]'
                                    : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                                }`}
                            onClick={() => handlePaymentMethodChange(method.id)}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value={method.id}
                                        checked={selectedPaymentMethod === method.id}
                                        onChange={() => handlePaymentMethodChange(method.id)}
                                        className="w-5 h-5 text-blue-600 bg-blue-600 border-gray-300 focus:ring-blue-500 focus:ring-2 accent-blue-600"
                                    />

                                    {/* Method Icon */}
                                    <div className={`w-12 h-12 rounded-lg ${method.bgColor} ${method.borderColor} border flex items-center justify-center mx-3`}>
                                        <FontAwesomeIcon
                                            icon={method.icon}
                                            className={`${method.color} text-xl`}
                                        />
                                    </div>

                                    {/* Method Name and Description - Mobile Optimized */}
                                    <div className="flex-1 min-w-0">
                                        <h5 className="font-semibold text-gray-800 text-sm sm:text-base lg:text-lg truncate">{method.label}</h5>
                                        <p className="text-gray-600 text-xs sm:text-sm line-clamp-1 sm:line-clamp-2 leading-tight">
                                            {method.id === 'instapay' && 'دفع فوري عبر InstaPay'}
                                            {method.id === 'bankTransfer' && 'تحويل بنكي مباشر'}
                                            {method.id === 'cash' && 'دفع نقدي عند الاستلام'}
                                            {method.id === 'digitalWallet' && 'محفظة رقمية'}
                                        </p>
                                    </div>
                                </div>

                                {/* Expand Arrow - Better mobile touch target */}
                                <div className={`transition-transform duration-300 flex-shrink-0 ml-2 p-2 rounded-full hover:bg-gray-100 ${showPaymentDetails[method.id] && selectedPaymentMethod === method.id ? 'rotate-180' : ''}`}>
                                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>

                            {/* Selected Indicator - Enhanced */}
                            {selectedPaymentMethod === method.id && (
                                <div className="absolute top-2 left-2">
                                    <div className="w-2 h-2 sm:w-3 sm:h-3 bg-blue-600 rounded-full animate-pulse shadow-sm"></div>
                                </div>
                            )}
                        </div>

                        {/* Enhanced Payment Details */}
                        {renderPaymentDetails(method)}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PaymentMethods;